(function() {
  'use strict';

  // ===========================================
  // BOLD SWATCH TO DROPDOWN CONVERTER
  // Converts BOLD Product Options swatch grids
  // into clean dropdown selects with inline swatches
  // ===========================================

  // Parse badges from option names like "Carbon Fiber (Best Seller)" or "Option [Popular]"
  function parseBadges(text) {
    if (!text) return { name: text, badges: [] };
    
    const badges = [];
    let name = text;
    
    // Match patterns like (Best Seller), [Popular], (New), etc.
    const badgePatterns = [
      /\s*\(Best Seller\)\s*/gi,
      /\s*\[Best Seller\]\s*/gi,
      /\s*\(Popular\)\s*/gi,
      /\s*\[Popular\]\s*/gi,
      /\s*\(New\)\s*/gi,
      /\s*\[New\]\s*/gi,
      /\s*\(Sale\)\s*/gi,
      /\s*\[Sale\]\s*/gi,
      /\s*\(Limited\)\s*/gi,
      /\s*\[Limited\]\s*/gi,
      /\s*\(Old Version\)\s*/gi,
      /\s*\[Old Version\]\s*/gi,
      /\s*\(Backorder\)\s*/gi,
      /\s*\[Backorder\]\s*/gi
    ];
    
    const badgeTypes = {
      'best seller': { text: 'Best Seller', class: 'badge--best-seller' },
      'popular': { text: 'Popular', class: 'badge--popular' },
      'new': { text: 'New', class: 'badge--new' },
      'sale': { text: 'Sale', class: 'badge--sale' },
      'limited': { text: 'Limited', class: 'badge--limited' },
      'old version': { text: 'Old Version', class: 'badge--old-version' },
      'backorder': { text: 'Backorder', class: 'badge--backordered' }
    };
    
    badgePatterns.forEach(pattern => {
      const match = name.match(pattern);
      if (match) {
        const badgeText = match[0].replace(/[\(\)\[\]]/g, '').trim().toLowerCase();
        if (badgeTypes[badgeText]) {
          badges.push(badgeTypes[badgeText]);
        }
        name = name.replace(pattern, '').trim();
      }
    });
    
    return { name, badges };
  }

  // Extract color from option name for border highlighting
  function getColorFromName(name) {
    const colorMap = {
      'red': '#c72222',
      'blue': '#1365e6',
      'green': '#2e7d32',
      'orange': '#f0720e',
      'yellow': '#c9a200',
      'purple': '#8500df',
      'gold': '#d4a000',
      'silver': '#a0a0a0',
      'black': '#333333',
      'white': '#cccccc',
      'carbon': '#444444',
      'brushed': '#555555'
    };
    
    for (const [colorName, colorValue] of Object.entries(colorMap)) {
      if (name.includes(colorName)) {
        return colorValue;
      }
    }
    
    return '#333333'; // Default fallback
  }

  function createDropdownFromSelect(boldOption) {
    // Skip if already converted
    if (boldOption.classList.contains('swatch-dropdown-converted')) return;
    
    // Get the title (may be inside a label)
    const titleEl = boldOption.querySelector('.bold_option_title');
    const title = titleEl ? titleEl.textContent.trim() : 'Option';
    
    // Get the original select element
    const originalSelect = boldOption.querySelector('select');
    if (!originalSelect) return;
    
    // Mark as converted and store title for CSS
    boldOption.classList.add('swatch-dropdown-converted');
    boldOption.setAttribute('data-title', title);
    
    // Hide the original select (not the whole container, just the select)
    originalSelect.style.display = 'none';
    
    // Create dropdown wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'swatch-dropdown';
    
    // Create the selected display
    const selected = document.createElement('div');
    selected.className = 'swatch-dropdown__selected';
    selected.setAttribute('tabindex', '0');
    selected.setAttribute('role', 'combobox');
    selected.setAttribute('aria-haspopup', 'listbox');
    
    // Create the dropdown menu
    const menu = document.createElement('div');
    menu.className = 'swatch-dropdown__menu';
    menu.setAttribute('role', 'listbox');
    
    // Build options from original select
    Array.from(originalSelect.options).forEach((option, index) => {
      // Skip placeholder options (empty value or "choose..." text)
      if (option.value === '' || option.textContent.toLowerCase().includes('choose')) {
        return;
      }
      
      const optionEl = document.createElement('div');
      optionEl.className = 'swatch-dropdown__option';
      optionEl.setAttribute('role', 'option');
      optionEl.dataset.index = index;
      optionEl.dataset.value = option.value;
      
      // Create text container
      const textContainer = document.createElement('span');
      textContainer.className = 'swatch-dropdown__text';
      
      const text = document.createElement('span');
      text.className = 'swatch-dropdown__title';
      text.textContent = option.textContent.trim();
      textContainer.appendChild(text);
      
      optionEl.appendChild(textContainer);
      
      // Check if this is currently selected
      if (option.selected && option.value !== '') {
        optionEl.classList.add('is-selected');
      }
      
      // Store reference to original option
      optionEl._originalOption = option;
      optionEl._originalSelect = originalSelect;
      
      menu.appendChild(optionEl);
    });
    
    wrapper.appendChild(selected);
    wrapper.appendChild(menu);
    
    // Set initial selected display
    const selectedOption = originalSelect.options[originalSelect.selectedIndex];
    // Check if BOLD already has a placeholder (like "choose..." or empty value)
    const isPlaceholder = !selectedOption || selectedOption.value === '' || selectedOption.textContent.toLowerCase().includes('choose');
    updateSelectDisplay(selected, isPlaceholder ? '' : selectedOption.textContent.trim(), !isPlaceholder);
    
    // Event: Toggle dropdown
    selected.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeAllDropdowns(wrapper);
      wrapper.classList.toggle('is-open');
    });
    
    // Event: Keyboard support
    selected.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        closeAllDropdowns(wrapper);
        wrapper.classList.toggle('is-open');
      } else if (e.key === 'Escape') {
        wrapper.classList.remove('is-open');
      }
    });
    
    // Event: Option selection
    menu.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const option = e.target.closest('.swatch-dropdown__option');
      if (option) {
        selectDropdownOption(wrapper, option, menu);
      }
    });
    
    // Insert dropdown after the select's parent (the label or bold_option_element)
    const insertAfter = originalSelect.closest('label') || originalSelect.closest('.bold_option_element') || originalSelect;
    insertAfter.insertAdjacentElement('afterend', wrapper);
  }

  function updateSelectDisplay(selectedEl, text, isSelected) {
    selectedEl.innerHTML = '';
    
    // If not selected, show placeholder
    if (!isSelected) {
      const placeholder = document.createElement('span');
      placeholder.className = 'swatch-dropdown__placeholder';
      placeholder.textContent = '-- Please Select --';
      selectedEl.appendChild(placeholder);
      return;
    }
    
    // Add checkmark for selected state
    const checkmark = document.createElement('span');
    checkmark.className = 'swatch-dropdown__checkmark';
    checkmark.textContent = '✓';
    selectedEl.appendChild(checkmark);
    
    const textContainer = document.createElement('span');
    textContainer.className = 'swatch-dropdown__text';
    
    const textSpan = document.createElement('span');
    textSpan.textContent = text;
    textContainer.appendChild(textSpan);
    
    selectedEl.appendChild(textContainer);
  }

  function selectDropdownOption(wrapper, optionEl, menu) {
    // Update visual state
    menu.querySelectorAll('.swatch-dropdown__option').forEach(opt => {
      opt.classList.remove('is-selected');
    });
    optionEl.classList.add('is-selected');
    
    // Update selected display
    const selected = wrapper.querySelector('.swatch-dropdown__selected');
    const text = optionEl.querySelector('.swatch-dropdown__title');
    updateSelectDisplay(selected, text ? text.textContent : '', true);
    
    // Add selection styling for non-swatch dropdowns
    wrapper.style.setProperty('--selected-color', '#333');
    wrapper.classList.add('has-selection');
    
    // Trigger loading animation
    wrapper.classList.remove('selection-flash', 'selection-complete');
    void wrapper.offsetWidth; // Force reflow
    wrapper.classList.add('selection-flash');
    
    // After animation completes, switch to solid underline
    setTimeout(() => {
      wrapper.classList.remove('selection-flash');
      wrapper.classList.add('selection-complete');
    }, 400);
    
    // Update the original select to maintain BOLD's logic
    if (optionEl._originalSelect && optionEl._originalOption) {
      optionEl._originalSelect.value = optionEl._originalOption.value;
      optionEl._originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Remove BOLD's error styling from parent container
    const boldOption = wrapper.closest('.bold_option');
    if (boldOption) {
      boldOption.classList.remove('bold_option_error');
      // Also check for any error elements BOLD may have added
      const errorElements = boldOption.querySelectorAll('.bold_option_error, .error, [class*="error"]');
      errorElements.forEach(el => el.classList.remove('bold_option_error', 'error'));
      // Hide error message text (don't remove, just hide so BOLD can still validate)
      const errorMessages = boldOption.querySelectorAll('.bold_option_error_message, .error-message, [class*="error_message"], [class*="error-message"]');
      errorMessages.forEach(el => el.style.display = 'none');
    }
    
    // Close dropdown
    wrapper.classList.remove('is-open');
  }

  function createDropdownFromSwatches(boldOption) {
    // Skip if already converted
    if (boldOption.classList.contains('swatch-dropdown-converted')) return;
    
    // Get the title
    const titleEl = boldOption.querySelector('.bold_option_title');
    const title = titleEl ? titleEl.textContent.trim() : 'Option';
    
    // Get all swatch values
    const swatchValues = boldOption.querySelectorAll('.bold_option_value');
    if (swatchValues.length === 0) return;
    
    // Mark as converted
    boldOption.classList.add('swatch-dropdown-converted');
    
    // Hide the original swatch element container
    const originalElement = boldOption.querySelector('.bold_option_element');
    if (originalElement) {
      originalElement.style.display = 'none';
    }
    
    // Create dropdown wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'swatch-dropdown';
    
    // Create the selected display
    const selected = document.createElement('div');
    selected.className = 'swatch-dropdown__selected';
    selected.setAttribute('tabindex', '0');
    selected.setAttribute('role', 'combobox');
    selected.setAttribute('aria-haspopup', 'listbox');
    
    // Create the dropdown menu
    const menu = document.createElement('div');
    menu.className = 'swatch-dropdown__menu';
    menu.setAttribute('role', 'listbox');
    
    // Track which option is currently selected
    let currentSelected = null;
    
    // Build options from BOLD swatches
    swatchValues.forEach((swatchValue, index) => {
      const valueElement = swatchValue.querySelector('.bold_option_value_element');
      const swatchSpan = swatchValue.querySelector('.bold_option_value_swatch span');
      const titleSpan = swatchValue.querySelector('.bold_option_value_title');
      const priceSpan = swatchValue.querySelector('.bold_option_value_price');
      const checkbox = swatchValue.querySelector('input[type="checkbox"]');
      
      // Only skip if we truly have no value element - don't skip based on missing swatch
      if (!valueElement) return;
      
      const optionEl = document.createElement('div');
      optionEl.className = 'swatch-dropdown__option';
      optionEl.setAttribute('role', 'option');
      optionEl.dataset.index = index;
      
      // Create swatch
      if (swatchSpan) {
        const swatch = document.createElement('span');
        swatch.className = 'swatch-dropdown__swatch';
        
        // Copy the background style from BOLD's swatch
        const bgImage = swatchSpan.style.backgroundImage;
        const bgColor = swatchSpan.style.backgroundColor;
        
        if (bgImage && bgImage !== 'none') {
          swatch.style.backgroundImage = bgImage;
          swatch.style.backgroundSize = 'cover';
          swatch.style.backgroundPosition = 'center';
        } else if (bgColor) {
          swatch.style.backgroundColor = bgColor;
        }
        
        // Add light border for light colors
        if (bgColor && isLightColor(bgColor)) {
          swatch.classList.add('swatch-dropdown__swatch--light');
        }
        
        optionEl.appendChild(swatch);
      }
      
      // Create text container
      const textContainer = document.createElement('span');
      textContainer.className = 'swatch-dropdown__text';
      
      // Add title - fallback to checkbox value if title span is missing
      const text = document.createElement('span');
      text.className = 'swatch-dropdown__title';
      
      let titleText = '';
      if (titleSpan && titleSpan.textContent.trim()) {
        titleText = titleSpan.textContent.trim();
      } else if (checkbox && checkbox.value) {
        titleText = checkbox.value;
      } else {
        titleText = 'Option ' + (index + 1);
      }
      
      // Parse badges from title
      const parsed = parseBadges(titleText);
      text.textContent = parsed.name;
      textContainer.appendChild(text);
      
      // Add badges
      parsed.badges.forEach(badge => {
        const badgeEl = document.createElement('span');
        badgeEl.className = 'swatch-dropdown__badge ' + badge.class;
        badgeEl.textContent = badge.text;
        textContainer.appendChild(badgeEl);
      });
      
      // Store clean name for display
      optionEl._cleanName = parsed.name;
      optionEl._badges = parsed.badges;
      
      // Add price if exists
      if (priceSpan && priceSpan.textContent.trim()) {
        const price = document.createElement('span');
        price.className = 'swatch-dropdown__price';
        price.textContent = ' +' + priceSpan.textContent.trim();
        textContainer.appendChild(price);
      }
      
      optionEl.appendChild(textContainer);
      
      // Check if this is currently selected
      if (valueElement.classList.contains('bold_swatch_selected')) {
        optionEl.classList.add('is-selected');
        currentSelected = { optionEl, swatchSpan, titleSpan, priceSpan, valueElement, checkboxValue: checkbox ? checkbox.value : '', cleanName: parsed.name, badges: parsed.badges };
      }
      
      // Store reference to original BOLD elements
      optionEl._boldValueElement = valueElement;
      optionEl._boldCheckbox = checkbox;
      optionEl._swatchSpan = swatchSpan;
      optionEl._titleSpan = titleSpan;
      optionEl._priceSpan = priceSpan;
      optionEl._checkboxValue = checkbox ? checkbox.value : '';
      
      menu.appendChild(optionEl);
    });
    
    wrapper.appendChild(selected);
    wrapper.appendChild(menu);
    
    // Set initial selected display
    if (currentSelected) {
      updateSelectedDisplay(selected, currentSelected.swatchSpan, currentSelected.titleSpan, currentSelected.priceSpan, currentSelected.checkboxValue, currentSelected.cleanName, currentSelected.badges, true);
    } else {
      // Default to placeholder
      updateSelectedDisplay(selected, null, null, null, null, null, null, false);
    }
    
    // Event: Toggle dropdown
    selected.addEventListener('click', (e) => {
      e.stopPropagation();
      closeAllDropdowns(wrapper);
      wrapper.classList.toggle('is-open');
    });
    
    // Event: Keyboard support
    selected.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        closeAllDropdowns(wrapper);
        wrapper.classList.toggle('is-open');
      } else if (e.key === 'Escape') {
        wrapper.classList.remove('is-open');
      }
    });
    
    // Event: Option selection
    menu.addEventListener('click', (e) => {
      const option = e.target.closest('.swatch-dropdown__option');
      if (option) {
        selectOption(wrapper, option, menu);
      }
    });
    
    // Insert dropdown after the title
    if (titleEl) {
      titleEl.insertAdjacentElement('afterend', wrapper);
    } else {
      boldOption.insertBefore(wrapper, originalElement);
    }
  }

  function updateSelectedDisplay(selectedEl, swatchSpan, titleSpan, priceSpan, checkboxValue, cleanName, badges, isSelected) {
    selectedEl.innerHTML = '';
    
    // If not selected, show placeholder
    if (!isSelected) {
      const placeholder = document.createElement('span');
      placeholder.className = 'swatch-dropdown__placeholder';
      placeholder.textContent = '-- Please Select --';
      selectedEl.appendChild(placeholder);
      return;
    }
    
    // Add checkmark for selected state
    const checkmark = document.createElement('span');
    checkmark.className = 'swatch-dropdown__checkmark';
    checkmark.textContent = '✓';
    selectedEl.appendChild(checkmark);
    
    // Add swatch
    if (swatchSpan) {
      const swatch = document.createElement('span');
      swatch.className = 'swatch-dropdown__swatch';
      
      const bgImage = swatchSpan.style.backgroundImage;
      const bgColor = swatchSpan.style.backgroundColor;
      
      if (bgImage && bgImage !== 'none') {
        swatch.style.backgroundImage = bgImage;
        swatch.style.backgroundSize = 'cover';
        swatch.style.backgroundPosition = 'center';
      } else if (bgColor) {
        swatch.style.backgroundColor = bgColor;
      }
      
      if (bgColor && isLightColor(bgColor)) {
        swatch.classList.add('swatch-dropdown__swatch--light');
      }
      
      selectedEl.appendChild(swatch);
    }
    
    // Add text
    const textContainer = document.createElement('span');
    textContainer.className = 'swatch-dropdown__text';
    
    const text = document.createElement('span');
    // Use clean name if provided, otherwise parse from titleSpan/checkboxValue
    if (cleanName) {
      text.textContent = cleanName;
    } else if (titleSpan && titleSpan.textContent.trim()) {
      const parsed = parseBadges(titleSpan.textContent.trim());
      text.textContent = parsed.name;
      badges = parsed.badges;
    } else if (checkboxValue) {
      const parsed = parseBadges(checkboxValue);
      text.textContent = parsed.name;
      badges = parsed.badges;
    } else {
      text.textContent = 'Select...';
    }
    textContainer.appendChild(text);
    
    // Add badges
    if (badges && badges.length > 0) {
      badges.forEach(badge => {
        const badgeEl = document.createElement('span');
        badgeEl.className = 'swatch-dropdown__badge ' + badge.class;
        badgeEl.textContent = badge.text;
        textContainer.appendChild(badgeEl);
      });
    }
    
    if (priceSpan && priceSpan.textContent.trim()) {
      const price = document.createElement('span');
      price.className = 'swatch-dropdown__price';
      price.textContent = ' +' + priceSpan.textContent.trim();
      textContainer.appendChild(price);
    }
    
    selectedEl.appendChild(textContainer);
  }

  function selectOption(wrapper, optionEl, menu) {
    // Check if this option is already selected (clicking to deselect)
    const wasSelected = optionEl.classList.contains('is-selected');
    
    // Update visual state
    menu.querySelectorAll('.swatch-dropdown__option').forEach(opt => {
      opt.classList.remove('is-selected');
    });
    
    // If it wasn't selected, select it now. If it was, leave it deselected.
    if (!wasSelected) {
      optionEl.classList.add('is-selected');
    }
    
    // Update selected display
    const selected = wrapper.querySelector('.swatch-dropdown__selected');
    
    if (wasSelected) {
      // Deselected - reset to placeholder
      updateSelectedDisplay(selected, null, null, null, null, null, null, false);
      // Remove selection styling
      wrapper.classList.remove('has-selection', 'selection-flash', 'selection-complete');
      wrapper.style.removeProperty('--selected-color');
    } else {
      // Selected - show the selection
      updateSelectedDisplay(selected, optionEl._swatchSpan, optionEl._titleSpan, optionEl._priceSpan, optionEl._checkboxValue, optionEl._cleanName, optionEl._badges, true);
      
      // Set color-coded border based on swatch color
      if (optionEl._swatchSpan) {
        const bgColor = optionEl._swatchSpan.style.backgroundColor;
        const bgImage = optionEl._swatchSpan.style.backgroundImage;
        
        // Also check for light colors to use a visible border
        let selectedColor = '#333';
        
        if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
          // Check if it's white/very light and needs a visible border
          const optionName = (optionEl._cleanName || '').toLowerCase();
          if (optionName.includes('white') && isLightColor(bgColor)) {
            selectedColor = '#cccccc';
          } else {
            selectedColor = bgColor;
          }
        } else if (bgImage && bgImage !== 'none') {
          // For image swatches, try to extract color from option name
          const optionName = (optionEl._cleanName || '').toLowerCase();
          selectedColor = getColorFromName(optionName);
        }
        
        wrapper.style.setProperty('--selected-color', selectedColor);
        wrapper.classList.add('has-selection');
        
        // Trigger loading animation
        wrapper.classList.remove('selection-flash', 'selection-complete');
        void wrapper.offsetWidth; // Force reflow
        wrapper.classList.add('selection-flash');
        
        // After animation completes, switch to solid underline
        setTimeout(() => {
          wrapper.classList.remove('selection-flash');
          wrapper.classList.add('selection-complete');
        }, 400);
      }
    }
    
    // Trigger click on BOLD's original element to maintain their logic
    if (optionEl._boldValueElement) {
      optionEl._boldValueElement.click();
    }
    
    // Remove BOLD's error styling from parent container
    const boldOption = wrapper.closest('.bold_option');
    if (boldOption) {
      boldOption.classList.remove('bold_option_error');
      const errorElements = boldOption.querySelectorAll('.bold_option_error, .error, [class*="error"]');
      errorElements.forEach(el => el.classList.remove('bold_option_error', 'error'));
      // Hide error message text (don't remove, just hide so BOLD can still validate)
      const errorMessages = boldOption.querySelectorAll('.bold_option_error_message, .error-message, [class*="error_message"], [class*="error-message"]');
      errorMessages.forEach(el => el.style.display = 'none');
    }
    
    // Close dropdown
    wrapper.classList.remove('is-open');
  }

  function closeAllDropdowns(except) {
    document.querySelectorAll('.swatch-dropdown.is-open').forEach(dropdown => {
      if (dropdown !== except) {
        dropdown.classList.remove('is-open');
      }
    });
  }

  function isLightColor(color) {
    // Parse rgb/rgba colors
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.7;
    }
    return false;
  }

  function init() {
    // Find all BOLD swatch option groups and convert them
    const swatchOptions = document.querySelectorAll('.bold_option_swatch');
    swatchOptions.forEach(createDropdownFromSwatches);
    
    // Find all BOLD dropdown option groups and convert them
    const dropdownOptions = document.querySelectorAll('.bold_option_dropdown');
    dropdownOptions.forEach(createDropdownFromSelect);
  }

  // Close dropdowns when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.swatch-dropdown')) {
      closeAllDropdowns();
    }
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize if BOLD loads options dynamically
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        setTimeout(init, 100);
      }
    });
  });

  // Start observing once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const boldContainer = document.querySelector('.bold_options');
      if (boldContainer) {
        observer.observe(boldContainer, { childList: true, subtree: true });
      }
    });
  } else {
    const boldContainer = document.querySelector('.bold_options');
    if (boldContainer) {
      observer.observe(boldContainer, { childList: true, subtree: true });
    }
  }
})();